import { ServicesManager } from '@ohif/core';
import classnames from 'classnames';
import React, { PropsWithChildren } from 'react';
import { usePredictions } from '../../../contexts/PredictionsContext';
import {
  PREDICTIONS_LOAD_FAILED_MESSAGE,
  usePredictionsLoadFailed,
  useAllPredictions,
  useDisplayedPredictions,
} from '../../../contexts/predictions.hooks';
import { DetectionsList } from '../DetectionsList';
import { TogglePredictionsDisplayButton } from '../TogglePredictionsDisplayButton';
import { SeriesPredictionEntry } from './SeriesPredictionEntry';

type PredictionsPanelProps = {
  servicesManager: ServicesManager;
  className?: string;
};

function EmptyPredictionsPanel({
  className,
  children,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <div
      className={classnames(
        'flex flex-auto flex-grow flex-col justify-around overflow-hidden text-base text-primary-light',
        className
      )}
    >
      <div className="flex justify-between p-2" data-testid="predictions-panel">
        {children}
      </div>
    </div>
  );
}

function NoPredictionsMatchingConfidences({
  className,
}: {
  className?: string;
}) {
  return (
    <div
      className={classnames(
        'flex flex-auto flex-col overflow-hidden',
        className
      )}
    >
      <div
        className="mt-4 flex-grow p-2 text-base text-primary-light"
        data-testid="predictions-panel"
      >
        No predictions are matching the confidences range
      </div>
    </div>
  );
}

export function PredictionsPanel({
  servicesManager,
  className,
}: PredictionsPanelProps) {
  const { isLoading } = usePredictions();
  const loadFailed = usePredictionsLoadFailed();
  const allPredictions = useAllPredictions();
  const displayedPredictions = useDisplayedPredictions();

  // a failed read first, as in the cleaning panel
  if (loadFailed) {
    return (
      <EmptyPredictionsPanel className={className}>
        {PREDICTIONS_LOAD_FAILED_MESSAGE}
      </EmptyPredictionsPanel>
    );
  }

  if (isLoading) {
    return (
      <EmptyPredictionsPanel className={className}>
        Loading predictions...
      </EmptyPredictionsPanel>
    );
  }

  if (!allPredictions || allPredictions.length === 0) {
    return (
      <EmptyPredictionsPanel className={className}>
        No predictions to display
      </EmptyPredictionsPanel>
    );
  }

  if (displayedPredictions.length === 0) {
    return <NoPredictionsMatchingConfidences className={className} />;
  }

  return (
    <div
      className={classnames(
        'flex flex-auto flex-col overflow-hidden text-white',
        className
      )}
      data-testid="predictions-panel"
    >
      <div className="flex flex-auto flex-col overflow-hidden">
        <TogglePredictionsDisplayButton />
        <DetectionsList
          servicesManager={servicesManager}
          renderDetection={({ index, detection, prediction }) => (
            <SeriesPredictionEntry
              index={index}
              prediction={prediction}
              detection={detection}
              key={`${detection.annotationId}-${index}`}
            />
          )}
        />
      </div>
    </div>
  );
}
